import React from "react";
import {
  Navigate,
  Outlet,
  // useLocation
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from 'react-router-dom';
import {
  AUTH_PREFIX_PATH,
  UNAUTHENTICATED_ENTRY,
  // REDIRECT_URL_KEY,
} from "configs/AppConfig";
import Utils from "utils";
import { signOut } from "store/slices/authSlice";
import { setCurrentProject } from "store/slices/projectSlice";

// URL에서 직접 프로젝트 ID를 추출하는 함수
const extractProjectIdFromUrl = (pathname, companyId) => {
  if (!companyId) return null;
  
  const parts = pathname.split('/').filter(Boolean);
  // URL 형식이 /{companyId}/{projectId}/... 인 경우
  if (parts.length >= 2 && parts[0] === companyId) {
    return parts[1];
  }
  
  return null;
};

const checkAccess = (menuItems, pathname, companyId, projectId) => {
  // menuItems가 유효한 배열인지 확인
  if (!menuItems || !Array.isArray(menuItems) || menuItems.length === 0) {
    return false;
  }

  // setting/profile_settings 또는 setting/project_settings/general 경로에 접근하려는 경우
  if (pathname.includes('/setting/profile_settings') || pathname.includes('/setting/project_settings/general')) {
    // URL에 프로젝트 ID가 있는 경우 (예: /{companyId}/{anyProjectId}/setting/profile_settings)
    const urlPathParts = pathname.split('/').filter(Boolean);
    if (urlPathParts.length >= 3 && urlPathParts[0] === companyId) {
      const urlProjectId = urlPathParts[1];
      
      // 프로젝트 ID가 다르더라도 설정 페이지는 접근 허용
      return true;
    }
    
    // 프로젝트 ID가 없는 경로라도 설정 페이지는 접근 허용
    return true;
  }
  
  // 경로에서 마지막에 UUID 또는 ID가 있는지 확인하기 위한 정규식
  const uuidRegex = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
  const idRegex = /\/\d+$/;
  
  // UUID 또는 ID를 제거한 경로
  let cleanedPath = pathname;
  if (uuidRegex.test(pathname)) {
    cleanedPath = pathname.replace(uuidRegex, '');
  } else if (idRegex.test(pathname)) {
    cleanedPath = pathname.replace(idRegex, '');
  }
  
  // operation/game_operation과 같은 복잡한 경로 처리
  if (pathname.includes('/operation/')) {
    const hasOperationMenu = menuItems.some(menu => {
      // 상위 메뉴 중 'operation'을 포함하는 것이 있는지 확인
      return menu.path && menu.path.includes('/operation');
    });
    
    // 동작 관련 메뉴가 없으면 접근 불가
    if (!hasOperationMenu) {
      return false;
    }
  }
  
  const checkPathMatch = (menuPath, currentPath) => {
    // 정확히 일치하는 경우
    if (currentPath === menuPath) return true;
    
    // 와일드카드 처리 (예: /path/* 형태의 메뉴 항목)
    if (menuPath.endsWith('/*') && currentPath.startsWith(menuPath.slice(0, -2))) return true;
    
    // 경로 시작 부분이 일치하는지 확인 (ID나 UUID가 붙은 상세 페이지)
    if (currentPath.startsWith(menuPath + '/')) return true;
    
    return false;
  };
  
  const hasMenuAccess = menuItems.some(menu => {
    // 기본 경로 일치 확인
    if (checkPathMatch(menu.path, pathname) || checkPathMatch(menu.path, cleanedPath)) return true;
    
    // 하위 메뉴가 있으면 재귀적으로 체크
    if (menu.submenu && Array.isArray(menu.submenu) && menu.submenu.length > 0) {
      return checkAccess(menu.submenu, pathname, companyId, projectId);
    }

    return false;
  });
  
  return hasMenuAccess;
};

const ProtectedRoute = () => {
  const { token } = useSelector((state) => state.auth);
  const menuData = useSelector((state) => state.menu); 
  const location = useLocation();
  const companyId = localStorage.getItem("companyId");
  
  // URL에서 직접 프로젝트 ID 추출 (헤더에서 변경되었을 때도 최신 상태 유지)
  const urlProjectId = extractProjectIdFromUrl(location.pathname, companyId);
  // URL에서 추출한 프로젝트 ID가 있으면 사용, 없으면 Utils에서 가져옴
  const projectId = urlProjectId || Utils.getProjectId();
  
  // URL 경로를 분석하여 주요 세그먼트 추출
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const isValidRoute = pathSegments.length >= 3; // companyId, projectId, 그 외 경로가 최소 필요
  const mainPath = isValidRoute ? `/${pathSegments[2]}` : '';  // 주요 경로 (첫 번째 세그먼트)
  const subPath = isValidRoute && pathSegments.length > 3 ? `/${pathSegments[3]}` : ''; // 보조 경로 (두 번째 세그먼트)
  
  const dispatch = useDispatch();
  
  // 토큰이 없는 경우 처리
  if (!token) {
    // URL에서 companyId 추출
    const pathSegments = location.pathname.split('/').filter(Boolean);
    let urlCompanyId = '';
    
    // URL에서 companyId 추출 로직
    if (pathSegments.length >= 1 && pathSegments[0] !== 'auth') {
      urlCompanyId = pathSegments[0];
    }
    
    // URL에서 추출한 companyId가 있으면 사용, 없으면 localStorage의 값 사용
    const redirectCompanyId = urlCompanyId || companyId;
    
    return (
      <Navigate
        to={`${
          redirectCompanyId ? `/${redirectCompanyId}` : ""
        }${AUTH_PREFIX_PATH}${UNAUTHENTICATED_ENTRY}`}
        replace
      />
    );
  }
  
  // 메뉴 데이터가 아직 로드되지 않은 경우 잠시 대기 (상단으로 이동)
  if (!menuData || !Array.isArray(menuData) || menuData.length === 0) {
    return <Outlet />;
  }
  
  // 특정 operation 경로에 대한 직접적인 처리
  if (location.pathname.includes('/operation/game_operation/player')) {
    // operation 관련 메뉴 권한 확인
    const hasOperationAccess = menuData.some(menu => 
      (menu.path && menu.path.includes('/operation')) ||
      (menu.submenu && Array.isArray(menu.submenu) && menu.submenu.some(submenu => 
        submenu.path && submenu.path.includes('/operation')
      ))
    );
    
    // 권한이 없는 경우 에러 페이지로 강제 리다이렉트
    if (!hasOperationAccess) {
      // 강제 리다이렉트
      if (companyId && projectId) {
        window.location.href = `/${companyId}/${projectId}/error`;
        return null;
      } else if (companyId) {
        window.location.href = `/${companyId}/error`;
        return null;
      } else {
        window.location.href = `/error`;
        return null;
      }
    }
  }
  
  // 에러 페이지 경로 패턴 분석
  const pathParts = location.pathname.split('/').filter(Boolean);
  
  // /error 또는 /:companyId/error 패턴인 경우 로그아웃 처리 후 로그인 페이지로 이동
  if ((pathParts.length === 1 && pathParts[0] === 'error') || 
      (pathParts.length === 2 && pathParts[1] === 'error')) {
    // project ID 제거
    localStorage.removeItem("projectId");
    // state.project.currentProject 초기화
    dispatch(setCurrentProject(null));
    dispatch(signOut());
    
    // 기존 companyId를 유지한 채로 로그인 페이지로 이동
    const savedCompanyId = localStorage.getItem("companyId");
    return (
      <Navigate
        to={`${
          savedCompanyId ? `/${savedCompanyId}` : ""
        }${AUTH_PREFIX_PATH}${UNAUTHENTICATED_ENTRY}`}
        replace
      />
    );
  }
  
  // /:companyId/:projectId/error 패턴인 경우 에러 페이지 표시
  if (pathParts.length === 3 && pathParts[2] === 'error') {
    return <Outlet />;
  }
  
  // 대시보드 경로인 경우는 항상 허용 (기본 리다이렉트 경로이므로)
  if (location.pathname.includes('/dashboards/')) {
    return <Outlet />;
  }
  
  // 메인 페이지 경로인 경우도 항상 허용
  if (location.pathname.includes('/main')) {
    return <Outlet />;
  }
  
  // 메인 경로 추출하여 권한 검사
  if (isValidRoute && mainPath) {
    // 메인 경로에 해당하는 메뉴 권한이 있는지 확인
    const hasMainPathAccess = menuData.some(menu => {
      // 정확한 경로 매칭을 위해 메뉴 경로에서 companyId, projectId 제외한 부분 추출
      const menuMainPath = menu.path ? menu.path.split('/').filter(Boolean).slice(2).join('/') : '';
      return menuMainPath && mainPath.includes(menuMainPath);
    });
    
    if (!hasMainPathAccess) {
      // 강제 리다이렉트
      if (companyId && projectId) {
        window.location.href = `/${companyId}/${projectId}/error`;
        return null;
      } else if (companyId) {
        window.location.href = `/${companyId}/error`;
        return null;
      } else {
        window.location.href = `/error`;
        return null;
      }
    }
  }
  
  // 메뉴에 없는 경우 error 페이지로 이동
  const hasAccess = checkAccess(menuData, location.pathname, companyId, projectId);
  
  if (!hasAccess) {
    // 강제 리다이렉트 - 브라우저 히스토리 대신 window.location을 사용
    if (companyId && projectId) {
      window.location.href = `/${companyId}/${projectId}/error`;
      return null; // 리다이렉트 진행 중 아무것도 렌더링하지 않음
    } else if (companyId) {
      window.location.href = `/${companyId}/error`;
      return null;
    } else {
      window.location.href = `/error`;
      return null;
    }
  }

  return <Outlet />;
};

export default ProtectedRoute;
